import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircleIcon, ArrowLeftIcon, BriefcaseIcon, CheckIcon, CopyIcon, DownloadIcon, EyeIcon, Loader2Icon, MailIcon, SendIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { QuoteLinesEditor } from '../../components/admin/QuoteLinesEditor';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate, formatDateTime, telHref } from '../../utils/format';
import { QuoteEmailDialog } from '../../components/admin/QuoteEmailDialog';
import { QuotePdfPreview } from '../../components/admin/QuotePdfPreview';
import { downloadQuotePdf } from '../../utils/quotePdf';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteLine, QuoteStatus } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
const SENT_WARNING = 'Dette tilbud er allerede sendt. Ændringer kan betyde, at kunden har en tidligere version.';
export function QuoteDetail() {
  const {
    quoteId
  } = useParams();
  const navigate = useNavigate();
  const {
    quotes,
    cases,
    updateQuote,
    setQuoteStatus,
    duplicateQuote,
    deleteQuote,
    createCaseFromQuote,
    authLoading,
    lastWriteError,
    settings
  } = useAdminData();
  const quote = quotes.find(entry => entry.id === quoteId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  /** Kundeoplysninger og PDF-tekster — alt kan rettes før tilbuddet sendes. */
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [intro, setIntro] = useState('');
  const [terms, setTerms] = useState('');
  const [closing, setClosing] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState('');
  const [pdfPreview, setPdfPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!quote) return;
    setTitle(quote.title);
    setDescription(quote.description);
    setLines(quote.lines);
    setQuoteDate(quote.quoteDate);
    setValidUntil(quote.validUntil);
    setInternalNotes(quote.internalNotes);
    setCustomerNotes(quote.customerNotes);
    setCustomerName(quote.customerName);
    setCustomerEmail(quote.customerEmail);
    setCustomerPhone(quote.customerPhone);
    setCustomerAddress(quote.customerAddress);
    setIntro(quote.intro);
    setTerms(quote.terms);
    setClosing(quote.closing);
    setPaymentTerms(quote.paymentTerms);
  }, [quote?.id]);
  if (!quote) return <Navigate to="/admin/tilbud" replace data-id="element-1313" data-mp="39esrz2pl7zk" />;
  const status = effectiveStatus(quote);
  const linkedCase = cases.find(item => item.id === quote.caseId || item.quoteId === quote.id);
  const locked = quote.status === 'Accepteret';
  const history = [...(quote.history ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  async function handleSave() {
    if (saving) return;
    // Der skrives aldrig, mens Firebase stadig gendanner sessionen.
    if (authLoading) {
      setError('Sessionen indlæses stadig. Prøv igen om et øjeblik.');
      return;
    }
    if (locked) {
      const confirmed = window.confirm('Tilbuddet er accepteret af kunden. Er du sikker på, at du vil ændre pris og indhold?');
      if (!confirmed) return;
    }
    // Et allerede sendt tilbud må aldrig ændres i stilhed.
    if (quote.status === 'Sendt' && !window.confirm(`${SENT_WARNING}\n\nVil du gemme alligevel?`)) {
      return;
    }
    setSaving(true);
    setError('');
    const result = await updateQuote(quote.id, {
      title,
      description,
      lines,
      quoteDate,
      validUntil,
      internalNotes,
      customerNotes,
      customerName,
      customerEmail,
      customerPhone,
      customerAddress,
      intro,
      terms,
      closing,
      paymentTerms
    });
    setSaving(false);
    if (!result.ok) {
      setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      return;
    }
    setSaved(true);
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1314" data-mp="2xr8mki812t6m">
      <Link to="/admin/tilbud" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-1315" data-mp="2xoe06l5geyak">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1316" data-mp="37gs4nlnx2j0f" />
        Tilbud
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-1317" data-mp="2jr7mh4zl7ps7">
        <div data-id="element-1318" data-mp="2eso0j4kzudzu">
          <p className="text-sm text-ink-muted" data-id="element-1319" data-mp="2eswaanltl36t">{quote.quoteNumber}</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1320" data-mp="2tp5abrdjbkka">
            {quote.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-1321" data-mp="2os50r6hh7zof">
            {quote.customerName} · {formatKr(quote.total)} inkl. moms
          </p>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end" data-id="element-1322" data-mp="3yrwa5eg8bma">
          <QuoteStatusBadge status={status} data-id="element-1323" data-mp="b39kezim69oe" />
          <SelectField id="tilbud-status" label="Status" className="w-52" value={quote.status} onChange={async event => {
          setWorking('status');
          setError('');
          const result = await setQuoteStatus(quote.id, event.target.value as QuoteStatus);
          setWorking('');
          if (!result.ok) {
            setError(result.error ?? 'Statussen kunne ikke gemmes. Prøv igen.');
          }
        }} options={quoteStatuses.filter(option => option !== 'Udløbet').map(option => ({
          value: option,
          label: option
        }))} data-id="element-1324" data-mp="b2hy6fawpoas" />
        </div>
      </div>

      {sentTo && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-1325" data-mp="35ren96n1somd">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1326" data-mp="3aoeql9sijd9s">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1327" data-mp="1ntjopv2qdxmc" />
          </span>
          Tilbuddet er sendt til {sentTo}
        </p>}

      {quote.status === 'Sendt' && <p className="mt-5 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1328" data-mp="1iwjjfmdpgywa">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1329" data-mp="1dxq73xa5ndxp" />
          {SENT_WARNING}
        </p>}

      {status === 'Udløbet' && <p className="mt-5 flex items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1330" data-mp="143zjeulur7j2">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1331" data-mp="101uta5um4t16" />
          Tilbuddet er udløbet ({formatDate(quote.validUntil)}). Forlæng gyldigheden, eller lav en kopi.
        </p>}

      {quote.status === 'Accepteret' && <section className="mt-5 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-1332" data-mp="l7zobaxkvnv9">
          <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-1333" data-mp="2a1x8zcizni35">
            <div data-id="element-1334" data-mp="2e2z9rxsk0278">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1335" data-mp="2e1fsq4z2pn4e">
                {linkedCase ? 'Sag allerede oprettet' : 'Tilbuddet er accepteret'}
              </h2>
              <p className="mt-1 text-[15px] text-ink-soft" data-id="element-1336" data-mp="250zrz39psu38">
                {linkedCase ? `Sag #${linkedCase.number} — ${linkedCase.title}` : 'Opret sagen, så arbejdet kan planlægges og udføres.'}
              </p>
            </div>
            {linkedCase ? <Link to={`/admin/sager/${linkedCase.id}`} className={buttonClasses('secondary', 'md')} data-id="element-1337" data-mp="1hczqb8av7j9v">
                <BriefcaseIcon className="h-4 w-4" aria-hidden="true" data-id="element-1338" data-mp="1mai3xbztumo7" />
                Åbn sag
              </Link> : <Button size="md" disabled={working === 'case'} onClick={async () => {
          setWorking('case');
          setError('');
          const created = await createCaseFromQuote(quote);
          setWorking('');
          if (!created) {
            setError('Sagen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/sager/${created.id}`);
        }} data-id="element-1339" data-mp="17eso5vl8j5rx">
                {working === 'case' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1340" data-mp="1bo38uidpyruk" />
                    Opretter …
                  </> : 'Opret sag fra tilbud'}
              </Button>}
          </div>
        </section>}

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.5fr_0.5fr]" data-id="element-1341" data-mp="1gho8sfyvhvwe">
        <div className="space-y-5" data-id="element-1342" data-mp="1vf89haoql4wr">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1343" data-mp="1vc2lxzvrab35">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1344" data-mp="1qgna1dhb6p9d">
              Kunde på tilbuddet
            </h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1345" data-mp="655x7etp6xcq">
              Oplysningerne her vises på PDF’en og kan rettes uden at ændre kundekartoteket.
            </p>
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1346" data-mp="3sjffr7ay5b5l">
              <TextField id="td-kundenavn" label="Navn" value={customerName} onChange={event => {
              setCustomerName(event.target.value);
              setSaved(false);
            }} data-id="element-1347" data-mp="3ohu75y5g3j17" />
              <TextField id="td-kundeadresse" label="Adresse" value={customerAddress} onChange={event => {
              setCustomerAddress(event.target.value);
              setSaved(false);
            }} data-id="element-1348" data-mp="g18xef4zolx1" />
              <TextField id="td-kundemail" label="E-mail" type="email" value={customerEmail} onChange={event => {
              setCustomerEmail(event.target.value);
              setSaved(false);
            }} data-id="element-1349" data-mp="2vswovv18hm8g" />
              <TextField id="td-kundetlf" label="Telefon" type="tel" value={customerPhone} onChange={event => {
              setCustomerPhone(event.target.value);
              setSaved(false);
            }} data-id="element-1350" data-mp="1kb0td8g6dndf" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1351" data-mp="1z200ef1d43uw">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1352" data-mp="2401j2yuvm15u">Opgave</h2>
            {locked && <p className="mt-3 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1353" data-mp="27yitbr9lfrrh">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1354" data-mp="dmkglfc2sdqu" />
                Tilbuddet er accepteret. Ændringer i pris og indhold skal bekræftes, før de gemmes.
              </p>}
            <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1355" data-mp="1bzgdh6ver4hg">
              <TextField id="td-titel" label="Tilbudstitel" className="sm:col-span-2" value={title} onChange={event => {
              setTitle(event.target.value);
              setSaved(false);
            }} data-id="element-1356" data-mp="1c0zq8nhuuxeh" />
              <TextField id="td-dato" label="Tilbudsdato" type="date" value={quoteDate} onChange={event => {
              setQuoteDate(event.target.value);
              setSaved(false);
            }} data-id="element-1357" data-mp="1uxspr1hm8uh9" />
              <TextField id="td-gyldig" label="Gyldig til" type="date" value={validUntil} onChange={event => {
              setValidUntil(event.target.value);
              setSaved(false);
            }} data-id="element-1358" data-mp="2imbux85dcr54" />
            </div>
            <TextArea id="td-beskrivelse" label="Beskrivelse af arbejdet" className="mt-5" rows={4} value={description} onChange={event => {
            setDescription(event.target.value);
            setSaved(false);
          }} data-id="element-1359" data-mp="328dexo30rzax" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1360" data-mp="2jajalncu6byv">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1361" data-mp="24l4zoatm3mhd">Tilbudslinjer</h2>
            <div className="mt-5" data-id="element-1362" data-mp="29k7tt08208ak">
              <QuoteLinesEditor lines={lines} onChange={next => {
              setLines(next);
              setSaved(false);
            }} data-id="element-1363" data-mp="29l9hq5a64p9p" />
            </div>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1364" data-mp="20lmd7am573cf">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1365" data-mp="1sic23732dfxk">Noter</h2>
            <TextArea id="td-kundenoter" label="Bemærkninger til kunden" className="mt-5" rows={3} value={customerNotes} onChange={event => {
            setCustomerNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1366" data-mp="1nl22iylynnw0" />
            <TextArea id="td-internenoter" label="Interne noter" hint="Vises aldrig på kundens PDF." className="mt-5" rows={3} value={internalNotes} onChange={event => {
            setInternalNotes(event.target.value);
            setSaved(false);
          }} data-id="element-1367" data-mp="14t9b38mjiaqe" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1368" data-mp="2q6m4hn6ri5p7">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1369" data-mp="2q4hq2bb8loao">Tekst på tilbud</h2>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1370" data-mp="bx0fhmj054wt">
              Teksterne herunder står på PDF’en og kan skrives individuelt for hvert tilbud.
            </p>
            <TextArea id="td-indledning" label="Indledning" className="mt-5" rows={3} value={intro} onChange={event => {
            setIntro(event.target.value);
            setSaved(false);
          }} data-id="element-1371" data-mp="lpxtbfd1u5p3" />
            <TextArea id="td-betingelser" label="Betingelser" className="mt-5" rows={3} value={terms} onChange={event => {
            setTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1372" data-mp="3u4ptpbbic4hk" />
            <TextField id="td-betaling" label="Betalingsbetingelser" className="mt-5" value={paymentTerms} onChange={event => {
            setPaymentTerms(event.target.value);
            setSaved(false);
          }} data-id="element-1373" data-mp="1di6jy2m6k3tr" />
            <TextArea id="td-afslutning" label="Afsluttende tekst" className="mt-5" rows={2} value={closing} onChange={event => {
            setClosing(event.target.value);
            setSaved(false);
          }} data-id="element-1374" data-mp="3og7tqoatcnul" />
          </section>

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1375" data-mp="2quaaupsiugi4">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1376" data-mp="35iy8r2pub2b8" />
              {error}
            </p>}

          <div className="flex flex-wrap items-center gap-3" data-id="element-1377" data-mp="2d12l73o03v32">
            <Button size="lg" onClick={handleSave} disabled={saving} data-id="element-1378" data-mp="2cz809pcqc2yz">
              {saving ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1379" data-mp="28054ywmjxb47" />
                  Gemmer …
                </> : 'Gem ændringer'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => setPdfPreview(true)} data-id="element-1380" data-mp="ejinsgxv1l01">
              <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-1381" data-mp="ehfqdbudopla" />
              Forhåndsvis PDF
            </Button>
            <Button variant="secondary" size="lg" disabled={downloading} onClick={async () => {
            setDownloading(true);
            setError('');
            try {
              await downloadQuotePdf(quote, settings);
            } catch {
              setError('PDF’en kunne ikke dannes. Prøv igen.');
            }
            setDownloading(false);
          }} data-id="element-1382" data-mp="16aohofmzmmgv">
              {downloading ? <>
                  <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1383" data-mp="3n03s3kfjrpwt" />
                  Danner PDF …
                </> : <>
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1384" data-mp="dhmtkzt778xk" />
                  Download PDF
                </>}
            </Button>
            <Button variant="dark" size="lg" onClick={() => setComposing(true)} data-id="element-1385" data-mp="8l5w3f03zi7t">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-1386" data-mp="3ljk18w0o80l" />
              Send tilbud på mail
            </Button>
            {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1387" data-mp="w8w51emj47rs">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1388" data-mp="rhxnyctpv5nh">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1389" data-mp="mjme423f8v59" />
                </span>
                Ændringerne er gemt
              </p>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-1390" data-mp="1f7i8h05iqayv">
            <Button variant="secondary" size="md" disabled={working === 'accept' || quote.status === 'Accepteret'} onClick={async () => {
            setWorking('accept');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Accepteret');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1391" data-mp="1f673ix0c6x66">
              Marker som accepteret
            </Button>
            <Button variant="secondary" size="md" disabled={working === 'reject' || quote.status === 'Afvist'} onClick={async () => {
            setWorking('reject');
            setError('');
            const result = await setQuoteStatus(quote.id, 'Afvist');
            setWorking('');
            if (!result.ok) setError(result.error ?? 'Statussen kunne ikke gemmes.');
          }} data-id="element-1392" data-mp="2agc1zflwzcys">
              Marker som afvist
            </Button>
            <button type="button" onClick={() => setConfirmingDelete(true)} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-line px-4 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-1393" data-mp="3so8jzb2jjlz3">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-1394" data-mp="afhlcr74qnjy" />
              Slet tilbud
            </button>
          </div>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1395" data-mp="kfi348pcovm1">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1396" data-mp="kdf8tch60gbi">Historik</h2>
            <ol className="mt-4 space-y-3" data-id="element-1397" data-mp="p8uids853q24">
              {history.map(event => <li key={event.id} className="flex gap-3 text-[15px]" data-id="element-1398" data-mp="u7nv3iflrd18">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" data-id="element-1399" data-mp="y8hhu8hb6lsj" />
                  <span data-id="element-1400" data-mp="2notl8pbyuh7a">
                    <span className="text-ink" data-id="element-1401" data-mp="2np3ceg8qyuek">{event.text}</span>
                    <span className="block text-sm text-ink-muted" data-id="element-1402" data-mp="2ipgy03h6n846">
                      {formatDateTime(event.at)}
                      {event.by ? ` · ${event.by}` : ''}
                    </span>
                  </span>
                </li>)}
              {history.length === 0 && <li className="text-sm text-ink-muted" data-id="element-1403" data-mp="36fbbmn9bt0ph">Ingen aktivitet registreret endnu.</li>}
            </ol>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-1404" data-mp="32faxz4u1wtjh">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-1405" data-mp="2xly9cupdgmqq">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1406" data-mp="2snem8qz1l4ws">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-1407" data-mp="3q2sh6bz92jau">
              {quote.customerName || '—'}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-1408" data-mp="3lb0y0mifbfuq">
              <li data-id="element-1409" data-mp="82esd4jcij7a">{quote.customerPhone || '—'}</li>
              <li className="break-all" data-id="element-1410" data-mp="83orzy5ujzg1">{quote.customerEmail || '—'}</li>
              <li data-id="element-1411" data-mp="d0yi7g128f4x">{quote.customerAddress || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-1412" data-mp="3vg9388ywpygd">
              {quote.customerPhone && <LinkButton href={telHref(quote.customerPhone)} variant="secondary" size="sm" data-id="element-1413" data-mp="3boppz2b0uga">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1414" data-mp="3lha0f70tox1a" />
                  Ring
                </LinkButton>}
              {quote.customerEmail && <LinkButton href={`mailto:${quote.customerEmail}`} variant="secondary" size="sm" data-id="element-1415" data-mp="3ch4uz2f6w8pr">
                  <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1416" data-mp="3giqev7q55n9g" />
                  Send e-mail
                </LinkButton>}
            </div>
            {quote.customerId && <Link to={`/admin/kunder/${quote.customerId}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-1417" data-mp="1erd9khk9hdcv">
                Se kundeprofil
              </Link>}
          </section>

          {linkedCase && <section className="rounded-card border border-line bg-white p-6" data-id="element-1418" data-mp="38yxl7r4iio37">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1419" data-mp="3911zn301f5hq">
                Tilknyttet sag
              </h2>
              <p className="mt-3 text-[15px] text-ink" data-id="element-1420" data-mp="3iwls1ffe7m1q">
                Sag #{linkedCase.number} — {linkedCase.title}
              </p>
              <Link to={`/admin/sager/${linkedCase.id}`} className={`${buttonClasses('secondary', 'sm')} mt-4`} data-id="element-1421" data-mp="3dy0rtg0552da">
                Åbn sag
              </Link>
            </section>}

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1422" data-mp="1xjsfsfczybfw">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1423" data-mp="1xlvh3vjlnqzx">Beløb</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-1424" data-mp="1snbsfndmg13v">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1425" data-mp="1uz2sqnmzxqjq">
                <dt className="text-ink-soft" data-id="element-1426" data-mp="1z0g237pasbve">Ekskl. moms</dt>
                <dd className="font-medium text-ink" data-id="element-1427" data-mp="1z32a18g3ote5">{formatKr(quote.subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1428" data-mp="23yhg3e7v9l00">
                <dt className="text-ink-soft" data-id="element-1429" data-mp="28yvj2hum24r6">Moms</dt>
                <dd className="font-medium text-ink" data-id="element-1430" data-mp="1bc56aiygl2dz">{formatKr(quote.vatAmount)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-1431" data-mp="1ga6lutwoejkp">
                <dt className="font-medium text-ink" data-id="element-1432" data-mp="1g8de1babttb4">Total</dt>
                <dd className="font-display text-lg font-bold text-ink" data-id="element-1433" data-mp="1l8879lhkquzc">{formatKr(quote.total)}</dd>
              </div>
            </dl>
            {quote.lastSentAt ? <p className="mt-4 text-sm text-ink-muted" data-id="element-1434" data-mp="slx90hhfx65p">
                Sendt til {quote.sentTo} den {formatDateTime(quote.lastSentAt)}
              </p> : <p className="mt-4 text-sm text-ink-muted" data-id="element-1435" data-mp="q0pe0vvf0ils">Tilbuddet er endnu ikke sendt til kunden.</p>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-1436" data-mp="l2ny2jtaczej">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-1437" data-mp="g208m2kpgqmf">Handlinger</h2>
            <div className="mt-4 flex flex-col gap-2" data-id="element-1438" data-mp="b3yhqvh1fn0e">
              <Button variant="secondary" size="sm" disabled={working === 'copy'} onClick={async () => {
              setWorking('copy');
              setError('');
              const copy = await duplicateQuote(quote);
              setWorking('');
              if (!copy) {
                setError(lastWriteError() || 'Tilbuddet kunne ikke duplikeres. Prøv igen.');
                return;
              }
              navigate(`/admin/tilbud/${copy.id}`);
            }} data-id="element-1439" data-mp="b5j8ea08elsz">
                <CopyIcon className="h-4 w-4" aria-hidden="true" data-id="element-1440" data-mp="3fjri9l8mgf8a" />
                Dupliker tilbud
              </Button>
            </div>
          </section>
        </aside>
      </div>

      {pdfPreview && <QuotePdfPreview quote={quote} onClose={() => setPdfPreview(false)} data-id="element-1441" data-mp="39tjlby8o4rm1" />}

      {composing && <QuoteEmailDialog quote={quote} onClose={() => setComposing(false)} onSent={email => {
      setComposing(false);
      setSentTo(email);
    }} data-id="element-1442" data-mp="2vxy1e5haky0h" />}

      {confirmingDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-tilbud-titel" data-id="element-1443" data-mp="3smds44w51df6">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-1444" data-mp="3jssewpzmg47v">
            <h2 id="slet-tilbud-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-1445" data-mp="g6jyq9ucvyt6">
              Er du sikker på, at du vil slette dette tilbud?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-1446" data-mp="lfusa3aitz8d">
              {quote.quoteNumber} slettes permanent.
            </p>
            {linkedCase && <p className="mt-4 flex items-start gap-2 rounded-xl bg-accent-soft px-4 py-3 text-sm text-accent-deep" data-id="element-1447" data-mp="ubafi1grtg4z">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1448" data-mp="6l6nkrcy34tt" />
                Tilbuddet er knyttet til sag #{linkedCase.number}. Sagen bliver ikke slettet, men mister
                henvisningen til tilbuddet.
              </p>}
            {error && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-1449" data-mp="bi6j48c64p6o">
                {error}
              </p>}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-1450" data-mp="1s041e2prp2cr">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setError('');
            const result = await deleteQuote(quote.id);
            setDeleting(false);
            if (!result.ok) {
              setError(result.error ?? 'Tilbuddet kunne ikke slettes. Prøv igen.');
              return;
            }
            navigate('/admin/tilbud');
          }} data-id="element-1451" data-mp="1myepylymumo5">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1452" data-mp="d0x4ea7lnt31" />
                    Sletter …
                  </> : 'Slet tilbud'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirmingDelete(false)} data-id="element-1453" data-mp="2et36b8iq5c41">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}
    </div>;
}